import React, { Component } from 'react'
import { Row, Col, Button, Spinner, Label, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import Dropzone from 'react-dropzone'
import { FileIcon } from 'react-file-icon'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import filesize from 'filesize'

export default class AddFilesCard extends Component {
  constructor(props) {
    super(props)
    this.state = {
      files: [],
      deleteFileIsLoading: false,
      deleteFileModalIsOpen: false,
      deleteFileId: null,
    }
  }

  componentWillUnmount() {
    // Make sure to revoke the data uris to avoid memory leaks
    if (this.state.files?.length > 0) {
      this.state.files.forEach(file => URL.revokeObjectURL(file.props.preview))
    }
    return null
  }

  handleOnDropFiles = (newAcceptedFiles) => {
    const { files } = this.state

    const acceptedFormatted = newAcceptedFiles?.map(file => {
      const icon = this.formatFileIcon(file.type)
      const extension = this.extractFileExtension(file.path)
      const name = (file.path).replace(/\.[^/.]+$/, '')

      return ({
        data: file,
        props: {
          id: uuidv4(),
          preview: URL.createObjectURL(file),
          modified: moment().format('DD/MM/YYYY HH:mm'),
          icon,
          extension,
          name,
        },
      })
    })

    this.setState({ files: files.concat(acceptedFormatted) })
  }

  formatFileIcon = type => {
    switch (type) {
      case 'application/pdf': return {
        color: '#ffcfcc',
        labelColor: '#FF2116',
        type: 'acrobat',
      }
      case 'image/png':
      case 'image/jpeg':
      case 'image/gif':
        return {
          color: '#ffe6cc',
          labelColor: '#ff9933',
          type: 'image',
        }
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': return {
        color: '#d7f4e4',
        labelColor: '#207245',
        type: 'spreadsheet',
      }
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': return {
        color: '#d7e3f4',
        labelColor: '#2a5699',
        type: 'document',
      }
      default: return {}
    }
  }

  extractFileExtension = path => {
    const fileExtensionPattern = /\.([0-9a-z]+)(?=[?#])|(\.)(?:[\w]+)$/gmi

    return path.match(fileExtensionPattern)[0].substring(1)
  }

  handleDeleteFile = id => {
    const { files } = this.state

    const filtered = files.filter(file => file.props.id !== id)

    this.setState({
      files: filtered,
      deleteFileId: null,
      deleteFileModalIsOpen: false,
      deleteFileIsLoading: false,
    })
  }

  toggleDeleteFileModal = id => {
    const { deleteFileIsLoading } = this.state

    if (deleteFileIsLoading) return null

    return this.setState(prevState => ({
      deleteFileModalIsOpen: !prevState.deleteFileModalIsOpen,
      deleteFileId: id || null,
    }))
  }

  render() {
    const { files, deleteFileIsLoading, deleteFileModalIsOpen, deleteFileId } = this.state

    return (
      <>
        <Modal
          centered
          isOpen={deleteFileModalIsOpen}
          toggle={() => this.toggleDeleteFileModal()}
        >
          <ModalHeader toggle={() => this.toggleDeleteFileModal()}>
            Excluir arquivo
          </ModalHeader>
          <ModalBody>
            Tem certeza que deseja excluir? Esta ação não poderá ser revertida.
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              onClick={() => this.toggleDeleteFileModal()}
              color="light"
              className="waves-effect"
              disabled={deleteFileIsLoading}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              color="danger"
              className="waves-effect waves-light"
              onClick={() => this.handleDeleteFile(deleteFileId)}
              disabled={deleteFileIsLoading}
            >
              Excluir
              {deleteFileIsLoading && (
                <Spinner size="sm" className="ml-2" />
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {files?.length > 0 && (
          <>
            {files.map((file) => (
              <div key={file.props.id} className="pt-1 pb-2">
                <Row className="d-flex align-items-start">
                  <Col className="d-flex">
                    <div style={{ width: 36 }} className="mr-3">
                      <FileIcon
                        extension={file.props.extension}
                        type={file.props.icon.type}
                        color={file.props.icon.color}
                        labelColor={file.props.icon.labelColor}
                      />
                    </div>
                    <div className="d-flex flex-column">
                      <h6 style={{ lineHeight: 1 }} className="mt-1">
                        {file.data.path}
                      </h6>
                      <Label>
                        {filesize(file.data.size, { locale: 'pt-BR', round: 0 })}
                      </Label>
                    </div>
                  </Col>

                  <Col className="d-flex justify-content-end" style={{ height: 35 }}>
                    <Button
                      href={file.props.preview}
                      download={file.data.path}
                      target="_blank"
                      rel="noreferrer"
                      color="light"
                      size="sm"
                      className="mr-3"
                      title="Baixar"
                    >
                      <i className="ri-download-2-fill font-size-16" />
                    </Button>

                    <Button
                      color="light"
                      size="sm"
                      title="Excluir"
                      onClick={() => this.toggleDeleteFileModal(file.props.id)}
                    >
                      <i className="ri-delete-bin-6-line font-size-16" />
                    </Button>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <hr />
                  </Col>
                </Row>
              </div>
            ))}
          </>
        )}

        <Row>
          <Col>
            <Dropzone
              multiple
              maxFiles={10}
              minSize={1}
              maxSize={10000000}
              accept="
                image/jpeg,
                image/png,
                application/pdf,
                application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                application/vnd.openxmlformats-officedocument.wordprocessingml.document
              "
              onDrop={(files) => this.handleOnDropFiles(files)}
            >
              {({ getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, isFocused }) => {
                const style = {
                  ...baseStyle,
                  ...(isDragActive ? activeStyle : {}),
                  ...(isDragAccept ? acceptStyle : {}),
                  ...(isDragReject ? rejectStyle : {}),
                  ...(isFocused ? focusStyle : {}),
                }

                return (
                  <div {...getRootProps({ style })} className="files-dropzone">
                    <input {...getInputProps()} />
                    <Row>
                      <Col>
                        <i
                          className="ri-upload-cloud-2-line mb-3"
                          style={{
                            fontSize: 46,
                            color: '#4aa3ff',
                            opacity: 0.8,
                          }}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <h6>
                          Arraste e solte ou clique para buscar
                        </h6>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <span>
                          Extensões permitidas: .jpg, .jpeg, .png, .pdf, .docx, .xlsx
                        </span>
                      </Col>
                    </Row>
                  </div>
                )
              }}
            </Dropzone>
          </Col>
        </Row>
      </>
    )
  }
}

const baseStyle = {
  cursor: 'pointer',
  padding: '20px 30px 30px 30px',
  borderWidth: 2,
  borderRadius: 6,
  borderColor: '#ccc',
  borderStyle: 'dashed',
  backgroundColor: '#f8f8f8',
  outline: 'none',
  transition: 'all .24s ease-in-out',
  textAlign: 'center',
}

const activeStyle = {
  borderColor: '#4aa3ff',
  color: '#4aa3ff',
}

const focusStyle = {}

const acceptStyle = {
  borderColor: 'green',
  color: 'green',
}

const rejectStyle = {
  borderColor: 'red',
  color: 'red',
}
